import type { ReactNode } from 'react';
import { useMemo, useRef } from 'react';
import { ApolloProvider } from '@apollo/client';
import type { MockedResponse } from '@apollo/client/testing';
import { MockedProvider } from '@apollo/client/testing';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';

import { createApolloClient } from '../../client';

interface QueryProviderProps {
  children: ReactNode;
  mocks?: MockedResponse[];
  addTypename?: boolean;
}

const ApolloProviderWithProjectContext = ({ children }: { children: ReactNode }) => {
  const projectContext = useProjectContext({ optional: true });
  const projectIdRef = useRef<string | undefined>(undefined);

  // Update the ref whenever project context changes
  projectIdRef.current = projectContext?.project._id;

  // Create Apollo client with project ID getter scoped to this instance
  const client = useMemo(
    () => createApolloClient(() => projectIdRef.current),
    [], // Empty deps - client is created once per provider instance
  );

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export const QueryProvider = ({ children, mocks = [], addTypename = false }: QueryProviderProps) => {
  if (__TEST__) {
    return (
      <MockedProvider mocks={mocks} addTypename={addTypename}>
        {children}
      </MockedProvider>
    );
  }

  return <ApolloProviderWithProjectContext>{children}</ApolloProviderWithProjectContext>;
};
